import { baseURL } from "@/utils/api";
import { authTokenManager } from "@/services/auth-token-manager";

/**
 * Response shape of POST /talents/ocr/extract-text.
 * Backend returns ApiRes wrapper: `{ data: { text: string }, message?: string }`.
 */
type TExtractTextResponse = {
  data: { text: string };
  message?: string;
};

/**
 * Lightweight OCR text extraction for a single uploaded file.
 * Used by the certificate 通知書/合格書 mismatch warning (IROHANA_MATCH_DEV-330).
 *
 * Silent-fail contract: backend returns `{ text: '' }` on Vision errors, so callers
 * never need to handle an "OCR failed" state explicitly — an empty string means no warning.
 * The caller should, however, pass an AbortSignal to cancel on unmount / file removal.
 */
export const postOcrExtractTextApi = async (file: File, signal?: AbortSignal): Promise<{ text: string }> => {
  const formData = new FormData();
  formData.append("file", file);

  const authToken = authTokenManager.getToken();
  const response = await fetch(`${baseURL}/talents/ocr/extract-text`, {
    method: "POST",
    headers: {
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    },
    body: formData,
    signal,
  });

  if (!response.ok) {
    // Non-2xx (size/MIME rejection, auth) → treat as no text, matching the silent-fail contract.
    return { text: "" };
  }

  const json = (await response.json()) as TExtractTextResponse;
  return { text: json?.data?.text ?? "" };
};

/**
 * Server-side cached OCR by (talentId, filePath). Used for files already persisted on
 * the BE — i.e. existing files in talent edit mode (IROHANA_MATCH_DEV-330 follow-up).
 *
 * First call OCRs and caches in the BE process; subsequent calls hit that cache.
 * Same silent-fail contract: non-2xx → `{ text: '' }`.
 */
export const postOcrExtractTextByPathApi = async (
  talentId: number,
  filePath: string,
  signal?: AbortSignal,
): Promise<{ text: string }> => {
  const authToken = authTokenManager.getToken();
  const response = await fetch(`${baseURL}/talents/${talentId}/ocr/extract-text-by-path`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    },
    body: JSON.stringify({ filePath }),
    signal,
  });

  if (!response.ok) {
    return { text: "" };
  }

  const json = (await response.json()) as TExtractTextResponse;
  return { text: json?.data?.text ?? "" };
};
